import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUserClubMemberships, getUserManageableClubs } from '../services/clubs';
import { canCreateClub, getLicenseInfo } from '../services/licenses';
import { UserClubMembership, ClubWithDetails } from '../types';
import Loading from '../components/Loading';
import './ClubsList.css';

const ClubsList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [memberships, setMemberships] = useState<UserClubMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canCreate, setCanCreate] = useState(false);
  const [licenseInfo, setLicenseInfo] = useState<any>(null);

  useEffect(() => {
    loadClubs();
  }, []);

  const loadClubs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [membershipsData, createPermission, license] = await Promise.all([
        getUserClubMemberships(),
        canCreateClub(),
        getLicenseInfo()
      ]);
      
      setMemberships(membershipsData);
      setCanCreate(createPermission.canCreate);
      setLicenseInfo(license);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load clubs');
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'owner': return 'role-badge owner';
      case 'admin': return 'role-badge admin';
      case 'member': return 'role-badge member';
      default: return 'role-badge member';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner': return t('clubs.roles.owner');
      case 'admin': return t('clubs.roles.admin');
      case 'member': return t('clubs.roles.member');
      default: return t('clubs.roles.member');
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="clubs-container">
      <div className="clubs-header">
        <div className="header-title">
          <h1>{t('clubs.title')}</h1>
          {licenseInfo && (
            <div className="license-info">
              <span className={`license-badge license-${licenseInfo.licenseType}`}>
                {t(`licenses.${licenseInfo.licenseType}`)}
              </span>
            </div>
          )}
        </div>
        {canCreate && (
          <Link to="/clubs/create" className="btn btn-primary">
            {t('clubs.createClub')}
          </Link>
        )}
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={loadClubs} className="btn btn-secondary">
            {t('common.retry')}
          </button>
        </div>
      )}

      {/* My Clubs Section */}
      <div className="clubs-section">
        <h2>{t('clubs.myClubs')}</h2>
        {memberships.length === 0 ? (
          <p className="no-clubs">{t('clubs.noClubs')}</p>
        ) : (
          <div className="clubs-grid">
            {memberships.map((membership) => (
              <div 
                key={membership.membership_id} 
                className="club-card clickable"
                onClick={() => navigate(`/clubs/${membership.club_id}/manage`)}
                style={{ cursor: 'pointer' }}
              >
                <div className="club-info">
                  <h3 className="club-name">{membership.club_name}</h3>
                  {membership.club_description && (
                    <p className="club-description">{membership.club_description}</p>
                  )}
                  <div className="club-meta">
                    <span className={getRoleBadgeClass(membership.role)}>
                      {getRoleLabel(membership.role)}
                    </span>
                    <span className="joined-date">
                      {t('clubs.joined')}: {new Date(membership.joined_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};

export default ClubsList;
